<script lang="ts">
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import Link from "../Link.svelte";
	import Icon from "../Icon.svelte";
	import trash from "../../../../core/assets/icons/trash.svg?raw";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import "../../../../core/assets/css/cart-product-grid.css";
	import type { OrderVoucher } from "../../../../core/schema/OrderVoucher.js";
	import { getVoucherPath } from "../../../../core/schema/paths/getVoucherPath.js";
	import NumberInput from "../Form/NumberInput.svelte";
	import StaticPicture from "../Picture/StaticPicture.svelte";
	import voucherImage from "../../../../core/assets/voucher.jpg";
	import { reportAddedVoucherToCart, reportRemovedVoucherFromCart } from "../../UniversalAnalytics.js";
	import { getCartStore } from "../../contexts/cartStoreContextKey.js";

	export let orderVoucher: OrderVoucher;
	export let index: number;
	$: ({ quantity } = orderVoucher);

	const cartStore = getCartStore();
	const { TENANT_LOCALE } = getGlobalSettings();

	$: quantityInputId = `vouchers.${index}.quantity`;

	async function onAmountChange({ detail: newQuantity }: CustomEvent<number>): Promise<void> {
		const oldQuantity = quantity;
		if (newQuantity < 1) {
			const confirmText = ["Opravdu chcete odebrat tento voucher?"];

			// eslint-disable-next-line no-alert
			const confirmed = confirm(confirmText.join(" "));
			if (confirmed) {
				await cartStore.removeVoucher(index);
				reportRemovedVoucherFromCart(orderVoucher, TENANT_LOCALE, oldQuantity);
				return;
			}
			const activeElement = document.activeElement as HTMLElement | undefined;
			if (activeElement?.id === quantityInputId) {
				activeElement.blur(); // Remove focus from the NumberInput so we don't cause an infinite loop if it is being held.
			}
			// Force update the quantity. The number input doesn't update if we don't change it here... :(
			quantity = newQuantity;
			quantity = oldQuantity;
			await cartStore.setVoucherQuantity(index, oldQuantity);
			return;
		}
		await cartStore.setVoucherQuantity(index, newQuantity);
		if (newQuantity > oldQuantity) {
			reportAddedVoucherToCart(orderVoucher, TENANT_LOCALE, newQuantity - oldQuantity);
		} else {
			reportRemovedVoucherFromCart(orderVoucher, TENANT_LOCALE, oldQuantity - newQuantity);
		}
	}

	async function onRemove(): Promise<void> {
		await cartStore.removeVoucher(index);
	}
</script>

<div class="cart-product-grid border-b-1 border-secondary-200 py-3 xl:py-6">
	<a
		href="/{getVoucherPath(TENANT_LOCALE, orderVoucher.orderVoucherProduct)}"
		class="grid-image group relative h-[3.5rem] w-full sm:h-[5rem] lg:h-[6.5rem] xl:h-[9rem]"
	>
		<div class="group flex h-full w-full items-center justify-center rounded-lg bg-secondary-40">
			<StaticPicture
				alt="Logo Sladká dílna"
				image={voucherImage}
				class="opacity-80 transition-all duration-300 group-hover:opacity-100"
				imgClass="rounded-lg"
				width={400}
				height={400}
			/>
		</div>
	</a>
	<Link
		class="grid-name mt-1 line-clamp-2 text-base font-semibold leading-[1.25] decoration-transparent hover:!decoration-secondary md:text-md xl:text-lg"
		href="/{getVoucherPath(TENANT_LOCALE, orderVoucher.orderVoucherProduct)}"
	>
		{orderVoucher.orderVoucherProduct.name[TENANT_LOCALE]}
	</Link>
	<div
		class="grid-calculation grid grid-cols-[5rem,6rem,auto] items-center gap-2 text-right md:h-fit md:grid-cols-[1fr,6rem,auto] lg:grid-cols-[1fr,7.5rem,auto] lg:gap-4 xl:grid-cols-[1fr,7.5rem,12rem] xl:gap-6"
	>
		<span class="text-3xs tracking-tighter min-[380px]:text-2xs lg:text-xs">
			{nbspify(formatPrice(orderVoucher.orderVoucherProduct.value, TENANT_LOCALE, true))}&nbsp;/&nbsp;ks
		</span>
		<div class="flex w-full gap-2">
			<NumberInput
				id={quantityInputId}
				class="!h-8 w-[6rem] !gap-1 !p-1 lg:!h-10 lg:w-[7.5rem] lg:!p-2"
				min={0}
				max={999}
				required
				value={quantity}
				on:change={onAmountChange}
			/>
		</div>
		<span class="font-bold tracking-tighter min-[380px]:text-md xl:text-lg">
			{nbspify(formatPrice(orderVoucher.orderVoucherProduct.value * quantity, TENANT_LOCALE, true))}
		</span>
	</div>
	<button
		class="grid-trash mt-1 flex h-fit w-full items-center justify-center sm:mt-2 lg:mt-3"
		type="button"
		on:click={onRemove}
	>
		<Icon
			icon={trash}
			class="aspect-square h-4 cursor-pointer text-secondary-250 transition-colors duration-300 hover:text-secondary-300"
		/>
	</button>
</div>
